import { useCallback, useMemo } from "react";
import type {
  BillingPlanId,
  BillingCheckoutConfig,
  BillingCheckoutTransaction,
} from "../types/billing";
import { useHttpAdapter } from "./useHttpAdapter";

interface ApiResponse<T> {
  status: string;
  data: T;
}

interface BillingCheckoutConfigDto {
  configured: boolean;
  environment: "sandbox" | "live";
  client_token: string;
  plan_id: BillingPlanId;
  price_label: string;
  price_ids?: Partial<Record<BillingPlanId, string>>;
  prices_configured?: Record<BillingPlanId, boolean>;
  beta_enabled?: boolean;
}

interface BillingCheckoutTransactionDto {
  transaction_id: string;
  environment: "sandbox" | "live";
  client_token: string;
}

export const useBillingApi = () => {
  const { get, post } = useHttpAdapter();

  const getCheckoutConfig = useCallback(async () => {
    try {
      const response = await get<ApiResponse<BillingCheckoutConfigDto>>(
        "/billing/checkout/config"
      );
      if (response.data?.status === "success" && response.data?.data) {
        return {
          configured: response.data.data.configured,
          environment: response.data.data.environment,
          clientToken: response.data.data.client_token,
          planId: response.data.data.plan_id,
          priceLabel: response.data.data.price_label,
          priceIds: response.data.data.price_ids ?? {},
          pricesConfigured: response.data.data.prices_configured ?? {
            pro_monthly: response.data.data.configured,
            agency_monthly: false,
          },
          betaEnabled: response.data.data.beta_enabled ?? false,
        } satisfies BillingCheckoutConfig;
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch billing checkout config", error);
      return null;
    }
  }, [get]);

  const createCheckoutTransaction = useCallback(async (planId: BillingPlanId) => {
    try {
      const response = await post<ApiResponse<BillingCheckoutTransactionDto>>(
        "/billing/checkout/transactions",
        { plan_id: planId }
      );
      if (response.data?.status === "success" && response.data?.data) {
        return {
          transactionId: response.data.data.transaction_id,
          environment: response.data.data.environment,
          clientToken: response.data.data.client_token,
        } satisfies BillingCheckoutTransaction;
      }
      return null;
    } catch (error) {
      console.error("Failed to create Paddle checkout transaction", error);
      return null;
    }
  }, [post]);

  return useMemo(
    () => ({
      getCheckoutConfig,
      createCheckoutTransaction,
    }),
    [createCheckoutTransaction, getCheckoutConfig]
  );
};

export default useBillingApi;
